import { useRouter } from 'expo-router';
import { useEffect } from 'react';
import { View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import mixpanelService from '@/core/clients/mixpanel';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { CreditCard } from '@/ui/icons/credit-card';
import { Dollar } from '@/ui/icons/dollar';
import { ShieldCheckmark } from '@/ui/icons/shield-checkmark';

const items = [
  {
    icon: <Dollar />,
    title: 'Paid every 2 weeks',
    caption: 'Earnings deposited on a biweekly schedule.',
  },
  {
    icon: <CreditCard />,
    title: 'Direct to your bank',
    caption: 'Sent by ACH. No checks, no waiting.',
  },
  {
    icon: <ShieldCheckmark />,
    title: 'Secure',
    caption: 'Encrypted and used only for payouts.',
  },
];

export default function BankAccountTutorial() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const setBankAccountTutorialSeen = useSettingsStore(
    (state) => state.setBankAccountTutorialSeen
  );

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Bank Account Tutorial',
    });
  }, []);

  const handleContinue = () => {
    setBankAccountTutorialSeen(true);
    router.replace('/partner/more/bank-account/connect');
  };

  return (
    <View style={styles.container}>
      <View style={styles.body}>
        <View style={styles.content}>
          <View style={{ gap: 8 }}>
            <Text type="title1">Add Your Bank To Get Paid</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              So we can send your rental earnings straight to your account.
            </Text>
          </View>

          <View style={{ gap: 16 }}>
            {items.map((item) => (
              <View key={item.title} style={styles.item}>
                <Icon
                  type="dressed"
                  icon={item.icon}
                  fillColor={theme.colors.icon.color}
                  width={20}
                  height={20}
                />

                <View style={{ flexShrink: 1 }}>
                  <Text type="bodyBold">{item.title}</Text>

                  <Text
                    type="caption1"
                    style={{ color: theme.colors.text.secondary }}
                  >
                    {item.caption}
                  </Text>
                </View>
              </View>
            ))}
          </View>

          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            US bank accounts only. Takes about 2 minutes.
          </Text>
        </View>

        <View style={styles.buttonDesktop}>
          <Button
            text="Add Bank Account"
            type="primary"
            onPress={handleContinue}
            style={{ width: 200 }}
          />
        </View>
      </View>

      <View style={styles.buttonMobile}>
        <Button
          text="Add Bank Account"
          type="primary"
          onPress={handleContinue}
        />
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background_1,
  },
  body: {
    flexGrow: 1,
    alignSelf: 'center',
    width: {
      xs: '100%',
      md: 600,
    },
    paddingHorizontal: 16,
    paddingTop: {
      xs: 16,
      md: 56,
    },
    gap: 32,
  },
  desktopNav: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  content: {
    gap: 32,
  },
  item: {
    flexDirection: 'row',
    gap: 12,
    alignItems: 'center',
  },
  buttonMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    padding: 16,
  },
  buttonDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    alignItems: {
      md: 'flex-end',
    },
  },
}));
